import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { getHyperlink } from 'utils/linkUtils';

import { setCookies } from 'modules/auth/actions';

import { showCookieSettings } from 'modules/auth/selectors';

import { CookiesSettings } from 'modules/auth/types/InitialState';

import { Modal } from 'components/ui';
import Button, { ButtonType } from 'components/ui/Button';

import CookieSettings from './Settings';
import { PRIVACY_POLICY_URL } from './config';

const Cookies: React.FC = () => {
  const dispatch = useDispatch();
  const acceptAllRef = React.useRef<HTMLButtonElement>(null);

  const showModal = useSelector(showCookieSettings);

  const [preferencesVisible, setPreferencesVisible] = React.useState(false);

  const handleShowPreferences = React.useCallback(() => setPreferencesVisible(true), []);

  const onSubmit = React.useCallback((settings:CookiesSettings[]) => {
    dispatch(setCookies(settings));
  }, [dispatch]);

  const handleAcceptAllOnClick = React.useCallback(() => {
    onSubmit(Object.values(CookiesSettings));
  }, [onSubmit]);

  const handleAcceptEssentialOnClick = React.useCallback(() => {
    onSubmit([CookiesSettings.ESSENTIAL]);
  }, [onSubmit]);

  const handleSettingsOnSubmit = React.useCallback((settings:CookiesSettings[]) => {
    onSubmit(settings);
    setPreferencesVisible(false);
  }, [onSubmit]);

  return (
    <>
      <Modal
        role="alertdialog"
        hideCloseButton
        focusRef={acceptAllRef}
        open={showModal && !preferencesVisible}
        className="modal-large max-h-[95vh] flex flex-col"
        title={(
          <div className="text-2xl text-default font-bold">
            <FormattedMessage id="cookies.title" defaultMessage="We value your privacy" />
          </div>
        )}
      >
        <div className="mt-8 text-justify max-h-1/2-screen overflow-y-auto scroll-smooth text-s">
          <p>
            <FormattedMessage
              id="cookies.content"
              defaultMessage='Fleetback uses cookies and other technologies to keep the website reliable and secure, and to measure performance. To do this, we collect information about users, their behaviour, and their devices. {linebreak}{linebreak} If you select "Accept all", you accept this and agree that we may share this information with third parties, such as our marketing partners and analytics services. Select "Set preferences" for further details and to manage your options.{linebreak}{linebreak}For more information, please read our <a>privacy notice</a>.'
              values={{
                a: getHyperlink({
                  target: '_blank',
                  className: 'font-bold text-default',
                  rel: 'noopener noreferrer',
                  'data-testid': 'general-terms',
                  href: PRIVACY_POLICY_URL,
                }),
                linebreak: <br />,
              }}
            />
          </p>
        </div>

        <div className="flex flex-col mt-8 gap-2 sm:gap-4">
          <Button
            ref={acceptAllRef}
            testId="accept-all"
            className="w-full"
            onClick={handleAcceptAllOnClick}
          >
            <FormattedMessage id="cookies.acceptAll" defaultMessage="Accept all" />
          </Button>
          <Button
            testId="preferences"
            type={ButtonType.SECONDARY}
            onClick={handleShowPreferences}
            className="w-full"
          >
            <FormattedMessage id="cookies.preferences" defaultMessage="Set preferences" />
          </Button>
          <Button
            testId="essential"
            type={ButtonType.SECONDARY}
            className="w-full"
            onClick={handleAcceptEssentialOnClick}
          >
            <FormattedMessage id="cookies.onlyEssential" defaultMessage="Only essential" />
          </Button>
        </div>
      </Modal>
      <CookieSettings open={preferencesVisible} onSubmit={handleSettingsOnSubmit} />
    </>
  );
};

export default Cookies;
